export type LensId = "operations" | "mobility" | "environment" | "earth" | "safety" | "vibes" | "agri" | "executive" | "intelligence";

export type LayerId =
  // Municipality core
  | "municipality-boundary"
  | "municipality-buildings"
  | "road-network"
  | "railway"
  // Agriculture & water (Sikhio-specific)
  | "lam-takhong"
  | "flood-risk-zones"
  | "waterways"
  | "water-bodies"
  | "distance-grid"
  // Live ops
  | "traffic-heatmap"
  | "incidents-itic"
  | "incidents-city-reports"
  | "cctv-cameras"
  // Open data
  | "datago-points"
  // Civic
  | "civic-points"
  | "attractions"
  | "health-tambons"
  // Sikhio heritage
  | "heritage-old-town"
  | "heritage-temple-spires"
  // 3D Tiles
  | "tile3d-buildings"
  // Air quality
  | "air4thai-stations"
  // GISTDA
  // News
  | "news-pins"
  // Imagery
  | "satellite-esri"
  | "satellite-viirs-truecolor"
  | "satellite-night"
  | "satellite-imerg"
  | "satellite-aerosol"
  | "satellite-no2"
  | "satellite-true-color"
  | "satellite-himawari"
  | "satellite-ndvi"
  | "gistda-flood-7d"
  | "gistda-flood-30d"
  | "gistda-flood-freq"
  | "gistda-dri"
  | "gistda-ndwi"
  | "satellite-soil-root"
  | "satellite-soil-surface"
  | "satellite-lst"
  | "satellite-flood"
  | "satellite-terrain"
  | "google-satellite"
  | "google-traffic"
  // Rendering-only — not in the ALL_LAYERS palette, but actively used
  | "municipality-boundary-line"
  | "municipality-boundary-fill";

export type MapViewState =
  | { kind: "lens"; lensId: LensId }
  | { kind: "custom"; label: string };

export function layerCanEnable(_id: LayerId): boolean {
  return true;
}

export interface Lens {
  id: LensId;
  label: string;
  /** Thai display label, shown under the English acronym in the picker. */
  labelTh: string;
  describe: string;
  layers: LayerId[];
}

export const LENSES: Lens[] = [
  {
    id: "executive",
    label: "EXEC",
    labelTh: "ผู้บริหาร",
    describe:
      "ภาพรวมเชิงกลยุทธ์ — เขตเทศบาล ภาพถ่ายดาวเทียม อาคาร จุดสนใจจากข้อมูลเปิด ภาพรวมเทศบาลเมืองสีคิ้ว (11.63 ตร.กม.)",
    layers: [
      "municipality-boundary-line",
      "municipality-buildings",
      "satellite-esri",
      "civic-points",
      "road-network",
      "railway",
      "waterways",
      "water-bodies",
      "lam-takhong",
      "flood-risk-zones",
      "datago-points",
      "attractions",
      // The municipality's 27 NT cameras are one of the few things here that
      // is genuinely theirs, and they asked for them to be used. They were
      // reachable only from OPS/MOB/ENV/SAF, so the Mayor's default landing
      // had no cameras at all and they read as "not in the system".
      "cctv-cameras",
    ],
  },
  {
    id: "operations",
    label: "OPS",
    labelTh: "ปฏิบัติการ",
    describe: "ปฏิบัติการ — อาคารทุกหลังแบบ 3 มิติ (ไล่สีตามประเภท) ถนน ทางรถไฟ จุดบริการประชาชน จราจรสด เหตุการณ์ กล้อง CCTV",
    layers: [
      "municipality-boundary-line",
      "municipality-buildings",
      "road-network",
      "railway",
      "civic-points",
      "traffic-heatmap",
      "incidents-city-reports",
      "incidents-itic",
      "cctv-cameras",
      "news-pins",
    ],
  },
  {
    id: "mobility",
    label: "MOB",
    labelTh: "คมนาคม",
    describe: "คมนาคม — ถนน ทางรถไฟสายตะวันออกเฉียงเหนือ ฮีทแมปจราจร เหตุการณ์ iTIC กล้อง CCTV",
    layers: [
      "municipality-boundary-line",
      "road-network",
      "railway",
      "traffic-heatmap",
      "incidents-itic",
      "cctv-cameras",
    ],
  },
  {
    id: "agri",
    label: "AGRI",
    labelTh: "เกษตร",
    describe: "เกษตรและน้ำ — ลุ่มน้ำลำตะคอง พื้นที่เสี่ยงน้ำท่วม ทางน้ำ ความชื้นในดินระดับรากพืชจาก SMAP พื้นที่สีเขียว NDVI ฝนดาวเทียม IMERG สำหรับวางแผนฤดูกาลและบริหารจัดการน้ำ",
    layers: [
      "municipality-boundary-line",
      "waterways",
      "lam-takhong",
      "flood-risk-zones",
      "satellite-ndvi",
      "satellite-imerg",
      "satellite-soil-root",
      "gistda-dri",
      "gistda-ndwi",
    ],
  },
  {
    id: "environment",
    label: "ENV",
    labelTh: "สิ่งแวดล้อม",
    describe: "สิ่งแวดล้อม — ภาพถ่ายดาวเทียม Esri พื้นที่เสี่ยงน้ำท่วมลำตะคอง ทางน้ำ คุณภาพอากาศ ความชื้นผิวดิน เปิดชั้น MODIS NDVI/LST/AOD เมื่อซูมออก",
    layers: [
      "municipality-boundary-line",
      "municipality-buildings",
      "satellite-esri",
      "flood-risk-zones",
      "waterways",
      "lam-takhong",
      "air4thai-stations",
      "satellite-soil-surface",
    ],
  },
  {
    id: "earth",
    label: "EAR",
    labelTh: "ภูมิอากาศ",
    describe: "เลนส์ EarthAlpha — สังเกตการณ์โลกสำหรับฝน น้ำท่วม ความร้อน หมอกควัน พื้นที่สีเขียว ความชื้นในดิน และทางน้ำ สำคัญมากช่วงมรสุมเพื่อเฝ้าระวังน้ำท่วมลำตะคอง",
    layers: [
      "municipality-boundary-line",
      "municipality-buildings",
      "satellite-esri",
      "satellite-imerg",
      "satellite-flood",
      "satellite-lst",
      "satellite-aerosol",
      "satellite-no2",
      "satellite-ndvi",
      "satellite-soil-root",
      "satellite-soil-surface",
      "waterways",
      "lam-takhong",
      "flood-risk-zones",
      "air4thai-stations",
    ],
  },
  {
    id: "safety",
    label: "SAF",
    labelTh: "ความปลอดภัย",
    describe: "ความปลอดภัย — พื้นที่เสี่ยงน้ำท่วมลำตะคอง เรื่องร้องเรียนประชาชน (Traffy) iTIC กล้อง CCTV ทางน้ำระบายน้ำ โรงพยาบาล+ดับเพลิง+ตำรวจ ตรวจจับน้ำท่วมจาก MODIS",
    layers: [
      "municipality-boundary-line",
      "municipality-buildings",
      "civic-points",
      "health-tambons",
      "waterways",
      "lam-takhong",
      "flood-risk-zones",
      "incidents-city-reports",
      "incidents-itic",
      "satellite-flood",
      "gistda-flood-7d",
      "cctv-cameras",
    ],
  },
  {
    id: "vibes",
    label: "VIB",
    labelTh: "นำเสนอ",
    describe: "มุมมองนำเสนอ — เขตเทศบาล + ภาพถ่ายดาวเทียมสีจริง MODIS + พื้นที่สีเขียว NDVI ใช้เมื่อนำเสนอภาพรวมสีคิ้ว มองเห็นเขาใหญ่ทางทิศตะวันตกเฉียงใต้",
    layers: ["municipality-boundary-line", "satellite-true-color", "satellite-ndvi"],
  },
  {
    id: "intelligence",
    label: "INT",
    labelTh: "พยากรณ์",
    describe: "ปัญญาประดิษฐ์เชิงบูรณาการ — การแจ้งเตือนพยากรณ์จาก TimesFM เชื่อมกับข้อมูลสังเกตการณ์โลก คลิกตัวชี้วัดพยากรณ์ในแถบซ้ายเพื่อเปิดชั้นแผนที่ การแจ้งเตือนที่ทำงานอยู่จะแสดงเป็นป้ายเหนือแผนที่",
    layers: [
      "municipality-boundary-line",
      "satellite-flood",
      "satellite-imerg",
      "satellite-ndvi",
      "flood-risk-zones",
      "incidents-city-reports",
      "waterways",
      "lam-takhong",
    ],
  },
];

export type LayerGroup = "municipality" | "agri-water" | "mobility" | "incidents" | "open-data" | "imagery" | "environment" | "gistda";

export const LAYER_GROUP_LABEL: Record<LayerGroup, string> = {
  municipality: "Municipality",
  "agri-water": "Agriculture & Water",
  mobility:     "Mobility",
  incidents:    "Incidents",
  "open-data":  "Open data",
  imagery:      "Imagery",
  environment:  "Environment",
  gistda:       "GISTDA ภัยพิบัติ",
};

// ─── Satellite freshness ───────────────────────────────────────────────
const SATELLITE_DELAY_DAYS: Partial<Record<LayerId, number>> = {
  "satellite-true-color":         1,
  "satellite-viirs-truecolor":    1,
  "satellite-night":              1,
  "satellite-himawari":           0,
  "satellite-imerg":              0,
  "satellite-ndvi":               8,
  "satellite-soil-root":          3,
  "satellite-soil-surface":       3,
  "satellite-lst":                1,
  "satellite-aerosol":            1,
  "satellite-no2":                1,
  "satellite-flood":              3,
  "satellite-esri":               0,
  "satellite-terrain":            0,
};

export function satelliteFreshness(id: LayerId): { label: string; date: string } | null {
  const d = SATELLITE_DELAY_DAYS[id];
  if (d == null) return null;
  const t = new Date();
  t.setUTCDate(t.getUTCDate() - d);
  const date = t.toISOString().slice(0, 10);
  if (d === 0) return { label: "LIVE", date };
  if (d === 1) return { label: "Y’DAY", date };
  return { label: `${d}D AGO`, date };
}

export const ALL_LAYERS: {
  id: LayerId;
  label: string;
  swatch: string;
  group: LayerGroup;
  describe: string;
}[] = [
  // ─── Municipality ──────────────────────────────────────────────────────
  { id: "municipality-boundary",  label: "Municipal boundary",        swatch: "#059669", group: "municipality",
    describe: "Sikhio Town Municipality outer boundary (เทศบาลเมืองสีคิ้ว) — 11.63 km²." },
  { id: "municipality-buildings", label: "Buildings (3D, colour-coded)", swatch: "#D97706", group: "municipality",
    describe: "~10k footprints (Microsoft ML + OSM tags). Colour by use: red=health, violet=school, gold=temple, teal=bank/office, amber=shop, terracotta=home." },
  { id: "tile3d-buildings",       label: "Buildings (3D Tiles)",       swatch: "#34D399", group: "municipality",
    describe: "Experimental OGC 3D Tiles stream. Toggle on to compare against the GeoJSON layer." },
  { id: "distance-grid",          label: "Distance grid (1·5·10 km)",  swatch: "#059669", group: "municipality",
    describe: "Concentric rings at 1 km / 5 km / 10 km from the municipal centroid. Reads as 'how far can fire/ambulance reach'." },

  // ─── Agriculture & Water (Sikhio-specific) ─────────────────────────────
  { id: "waterways",         label: "Waterways / canals",    swatch: "#0EA5E9", group: "agri-water",
    describe: "Hydrology network: Lam Takhong River (main watershed), irrigation canals (RID), streams and drains. Critical for flood-prevention planning and crop irrigation." },
  { id: "water-bodies",      label: "Lakes / reservoirs",         swatch: "#38BDF8", group: "agri-water",
    describe: "Standing water: lakes, ponds, reservoirs and basins inside the municipal envelope." },
  { id: "flood-risk-zones",  label: "Flood-risk zones",           swatch: "#EF4444", group: "agri-water",
    describe: "Historical flood extents including the 2010 event (287 mm, 24,785 households affected). Hover for severity + household count. Based on GISTDA/ARPN flood survey." },
  { id: "lam-takhong",       label: "Lam Takhong water network",  swatch: "#0EA5E9", group: "agri-water",
    describe: "Live water map: เขื่อนลำตะคอง dam (storage vs normal, ThaiWater daily) plus HII telemetry gauges within 35 km — water level colored by situation level, rain gauges by 24 h accumulation." },

  // ─── Mobility ──────────────────────────────────────────────────────────
  { id: "road-network",      label: "Roads (classified)", swatch: "#FB7185", group: "mobility",
    describe: "OSM road network coloured by class: Highway 2 (Mittraphap) and Highways 201/205 as primary; township roads as residential." },
  { id: "railway",           label: "SRT railway",               swatch: "#E2E8F0", group: "mobility",
    describe: "State Railway of Thailand Northeastern Line through Sikhio station." },
  { id: "traffic-heatmap",   label: "Traffic — by hour",         swatch: "#F87171", group: "mobility",
    describe: "Modelled traffic intensity weighted by hour-of-day + weekday/weekend. Mittraphap Road (Hwy 2) dominates." },
  { id: "cctv-cameras",      label: "CCTV cameras",              swatch: "#E5E7EB", group: "mobility",
    describe: "Public traffic cameras from Longdo inside the Sikhio bbox. Click for the live JPG/HLS stream." },

  // ─── Incidents ─────────────────────────────────────────────────────────
  { id: "incidents-itic",         label: "iTIC traffic events",     swatch: "#F59E0B", group: "incidents",
    describe: "Live iTIC / Longdo traffic events: accidents, closures, breakdowns. Bbox-filtered to Nakhon Ratchasima." },
  { id: "incidents-city-reports", label: "Citizen reports (Traffy)", swatch: "#B933AD", group: "incidents",
    describe: "Live citizen complaints from Traffy Fondue — Thailand's nationwide 311 channel." },

  // ─── Open data ─────────────────────────────────────────────────────────
  { id: "datago-points",     label: "data.go.th points",          swatch: "#B933AD", group: "open-data",
    describe: "Government POIs from data.go.th filtered to Sikhio/Nakhon Ratchasima: schools, hospitals, government offices, temples, markets." },

  // ─── Civic (OSM, municipality-wide) ────────────────────────────────────
  { id: "civic-points",      label: "Civic POIs (color-coded)",   swatch: "#EF4444", group: "municipality",
    describe: "Hospital (✚) · police · fire · government (เทศบาล) · temples · markets · schools · SRT station · JICA landfill · Khonburi Sugar Factory. Hover for name. From OSM." },
  { id: "attractions",      label: "Attractions & heritage",     swatch: "#D97706", group: "municipality",
    describe: "Tourism and heritage POIs in Sikhio district — เขื่อนลำตะคอง, the 4,000-year rock paintings at วัดเขาจันทน์งาม, เขายายเที่ยง viewpoint. Every coordinate verified against OpenStreetMap; sites that resolved outside the district were dropped." },
  { id: "health-tambons",   label: "Primary care by tambon",     swatch: "#DC2626", group: "municipality",
    describe: "MOPH 3-หมอ register for all 12 tambons of อำเภอสีคิ้ว. Circle area is population; colour is dependent elderly (ติดบ้าน + ติดเตียง) per 1,000 — where outreach is hardest, not just where most people live." },
  { id: "heritage-old-town", label: "Old town district",          swatch: "#D97706", group: "municipality",
    describe: "Historic old-town district along Mittraphap Road — commercial shophouses and civic core." },
  { id: "heritage-temple-spires", label: "Heritage sites + temples", swatch: "#D97706", group: "municipality",
    describe: "Heritage points: Wat Sikhio, JICA landfill (Thailand first), Lam Takhong Dam, Khonburi Sugar Factory, Khao Yai UNESCO boundary." },

  // ─── Air quality ───────────────────────────────────────────────────────
  { id: "air4thai-stations", label: "Air4Thai PCD stations",     swatch: "#22C55E", group: "environment",
    describe: "Official Thai government air-quality monitors for Nakhon Ratchasima province. Coloured by PM2.5, labelled with live national AQI." },

  // ─── GISTDA ────────────────────────────────────────────────────────────

  // ─── News ──────────────────────────────────────────────────────────────
  { id: "news-pins",         label: "Geocoded news pins",        swatch: "#EF4444", group: "incidents",
    describe: "News headlines mentioning a known Sikhio location are pinned on the map so the mayor sees exactly which site is referenced." },

  // ─── Imagery ───────────────────────────────────────────────────────────
  { id: "satellite-esri",    label: "Satellite (Esri HD)",      swatch: "#0039A6", group: "imagery",
    describe: "Esri World Imagery — high-res aerial / satellite mosaic. Shows cassava fields and Lam Takhong basin clearly." },
  { id: "google-satellite",  label: "Satellite (Google HD)",    swatch: "#34D399", group: "imagery",
    describe: "Google Maps satellite imagery via the Map Tiles API — often more recent and higher-res than Esri. Requires a Google Maps key." },
  { id: "google-traffic",    label: "Live Traffic (Google)",    swatch: "#F87171", group: "imagery",
    describe: "Google real-time traffic overlay (Map Tiles API). Dense over Bangkok and major corridors; provincial roads may show little flow data. Requires a Google Maps key." },
  { id: "satellite-terrain", label: "OpenTopoMap (zoom < 14)",  swatch: "#A3E635", group: "imagery",
    describe: "OpenTopoMap with contour lines and hillshade. Shows the Khao Yai massif topography well." },
  { id: "satellite-viirs-truecolor", label: "VIIRS true-color", swatch: "#A5F3FC", group: "imagery",
    describe: "VIIRS NOAA-20 corrected reflectance — daily, sharper than MODIS." },
  { id: "satellite-night",   label: "VIIRS night lights",       swatch: "#FACC15", group: "imagery",
    describe: "VIIRS Day/Night Band — shows the Mittraphap Road corridor and urban core at night." },
  { id: "satellite-true-color", label: "MODIS true-color",      swatch: "#93C5FD", group: "imagery",
    describe: "MODIS Terra corrected reflectance — daily 250 m. Shows Khao Yai green and the Lam Takhong basin." },
  { id: "satellite-himawari", label: "Himawari IR",             swatch: "#C7D2FE", group: "imagery",
    describe: "Himawari-9 Band 13 infrared — geostationary cloud loop, 10-min refresh. Tracks incoming monsoon cells." },
  { id: "satellite-imerg",   label: "IMERG rainfall",           swatch: "#06B6D4", group: "imagery",
    describe: "NASA IMERG half-hourly global precipitation. Watch monsoon cells build over Khorat Plateau — key for flood early warning." },
  { id: "satellite-ndvi",    label: "NDVI greenery",            swatch: "#34D399", group: "imagery",
    describe: "MODIS NDVI 8-day composite — vegetation greenness. Cassava/sugarcane seasonal cycles visible." },
  { id: "satellite-soil-root", label: "ความชื้นดิน · รากพืช",    swatch: "#0EA5E9", group: "environment",
    describe: "SMAP L4 root-zone soil moisture (0–100 cm) — the water cassava and sugarcane actually draw on. It falls before NDVI does, so it leads visible crop stress by weeks: the drought layer that matters for planting decisions." },
  { id: "satellite-soil-surface", label: "ความชื้นดิน · ผิวดิน",   swatch: "#38BDF8", group: "environment",
    describe: "SMAP L4 surface soil moisture (0–5 cm). Once this saturates, new rain runs straight off instead of soaking in — the physical layer behind FloodDash's \"ดินอิ่มน้ำ\" flood reasoning for Nakhon Ratchasima." },
  { id: "satellite-lst",     label: "Land surface temp",        swatch: "#F97316", group: "imagery",
    describe: "MODIS LST (day) — urban heat island. Agricultural fields show cooler than the Mittraphap Road strip." },
  { id: "satellite-aerosol", label: "Aerosol optical depth",    swatch: "#F472B6", group: "imagery",
    describe: "MODIS MAIAC AOD — haze from sugarcane burning season (Nov-Jan) visible as elevated AOD over the basin." },
  { id: "satellite-no2",     label: "NO₂ pollution (OMI)",      swatch: "#EF4444", group: "imagery",
    describe: "OMI tropospheric NO₂ — traffic and industrial sources along the Korat corridor." },
  { id: "satellite-flood",   label: "Flood detection",          swatch: "#38BDF8", group: "imagery",
    describe: "MODIS 3-day combined flood detection. Critical during monsoon — Lam Takhong basin is a known flood risk zone." },
  // GISTDA disaster platform — satellite-derived, official Thai source, keyed
  // via GISTDA_DISASTER_KEY. Served through our tile proxy so the key stays
  // server-side; the raster is the authoritative extent (clipped to viewport).
  { id: "gistda-flood-7d",   label: "GISTDA น้ำท่วม 7 วัน",      swatch: "#0039A6", group: "gistda",
    describe: "GISTDA satellite flood extent, latest 7-day pass. Official Thai disaster-platform source — the authoritative 'where is the water' overlay for Nakhon Ratchasima." },
  { id: "gistda-flood-30d",  label: "GISTDA น้ำท่วม 30 วัน",     swatch: "#0039A6", group: "gistda",
    describe: "GISTDA satellite flood extent, latest 30-day pass — the wider footprint of the current monsoon season." },
  { id: "gistda-flood-freq", label: "GISTDA น้ำท่วมซ้ำซาก",      swatch: "#B933AD", group: "gistda",
    describe: "GISTDA repeat-flood zones — where flooding recurs year on year. Planning context for drainage and land use, not a live reading." },
  { id: "gistda-dri",        label: "GISTDA เสี่ยงภัยแล้ง (DRIPlus)", swatch: "#D97706", group: "gistda",
    describe: "GISTDA DRIPlus drought-risk index, latest 7-day. Official drought-risk-by-area layer — the drought counterpart to the flood extent." },
  { id: "gistda-ndwi",       label: "GISTDA ความชื้นพืช (NDWI)", swatch: "#0EA5E9", group: "gistda",
    describe: "GISTDA NDWI vegetation-moisture index, latest 7-day — canopy water content, an early drought-stress signal for cassava and cane." },
];

/**
 * Layers whose render output is computed or decorative — no backing FeatureCollection.
 * The palette suppresses the count badge for these IDs.
 */
export const COMPUTED_LAYERS: ReadonlySet<LayerId> = new Set<LayerId>([
  "distance-grid",
  "tile3d-buildings",
  "heritage-old-town",
  "heritage-temple-spires",
  "google-satellite",
  "google-traffic",
  "satellite-esri",
  "satellite-true-color",
  "satellite-viirs-truecolor",
  "satellite-night",
  "satellite-ndvi",
  "satellite-lst",
  "satellite-aerosol",
  "satellite-no2",
  "satellite-flood",
  "satellite-terrain",
  "satellite-himawari",
  "satellite-imerg",
  "satellite-soil-root",
  "satellite-soil-surface",
  "gistda-flood-7d",
  "gistda-flood-30d",
  "gistda-flood-freq",
  "gistda-dri",
  "gistda-ndwi",
]);
